// utils/projectVisit.ts
export type ProjectVisit = {
  projectId: string;
  count: number;
  lastVisitedAt: number;
  projectState?: 'draft' | 'saved';
};

type VisitMap = Record<string, ProjectVisit>;

const STORAGE_KEY = 'projectVisits_v1';

function safeParse<T>(raw: string | null, fallback: T): T {
  try {
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function getProjectVisitsMap(): VisitMap {
  if (typeof window === 'undefined') return {};
  return safeParse<VisitMap>(localStorage.getItem(STORAGE_KEY), {});
}

/**
 * Returns visits as an array sorted by most recent.
 * This also solves your “objects inside objects” problem.
 */
export function getProjectVisits(): ProjectVisit[] {
  const map = getProjectVisitsMap();
  return Object.values(map).sort((a, b) => b.lastVisitedAt - a.lastVisitedAt);
}

export function recordProjectVisit(
  projectId: string,
  projectState?: 'draft' | 'saved'
) {
  if (typeof window === 'undefined') return;

  const map = getProjectVisitsMap();
  const prev = map[projectId];

  map[projectId] = {
    projectId,
    projectState: projectState ?? prev?.projectState,
    count: (prev?.count ?? 0) + 1,
    lastVisitedAt: Date.now(),
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore storage failures
  }
}

export function removeProjectVisit(projectId: string) {
  if (typeof window === 'undefined') return;

  const map = getProjectVisitsMap();
  delete map[projectId];

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore storage failures
  }
}

export function clearProjectVisits() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore storage failures
  }
}